import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import driverService from '../services/driverService';
import PageHeader from '../components/common/PageHeader';
import Loader from '../components/common/Loader';
import Badge from '../components/common/Badge';
import { formatCurrency, formatDate, formatDateTime } from '../utils/formatters';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';

function TrendBar({ value, max, color }) {
  const pct = max > 0 ? Math.max(4, Math.round((Number(value || 0) / max) * 100)) : 0;
  return (
    <div style={{ width: '100%', background: 'var(--gray-100)', borderRadius: 8, height: 10 }}>
      <div style={{ width: `${pct}%`, background: color, borderRadius: 8, height: 10 }} />
    </div>
  );
}

export default function DriverDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [kpi, setKpi] = useState({
    owner_monthly_profit_target: '',
    owner_max_expense_ratio_percent: '',
    driver_monthly_profit_goal: '',
    driver_target_trip_count: '',
    notes: '',
  });

  const isOwner = user?.role === 'owner';
  const isDriverSelf = user?.role === 'driver' && String(user?.id) === String(id);

  const load = async () => {
    setLoading(true);
    try {
      const insights = await driverService.getInsights(id);
      setData(insights);
      setKpi({
        owner_monthly_profit_target: insights.kpi_settings?.owner_monthly_profit_target || '',
        owner_max_expense_ratio_percent: insights.kpi_settings?.owner_max_expense_ratio_percent || '',
        driver_monthly_profit_goal: insights.kpi_settings?.driver_monthly_profit_goal || '',
        driver_target_trip_count: insights.kpi_settings?.driver_target_trip_count || '',
        notes: insights.kpi_settings?.notes || '',
      });
    } catch {
      toast.error('Failed to load driver detail');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const monthlyMax = useMemo(() => {
    const vals = (data?.trends || []).flatMap(t => [Number(t.revenue || 0), Number(t.expenses || 0), Number(t.profit || 0)]);
    return vals.length ? Math.max(...vals) : 0;
  }, [data]);

  const expenseMax = useMemo(() => {
    const vals = (data?.expense_trends || []).map(t => Number(t.total || 0));
    return vals.length ? Math.max(...vals) : 0;
  }, [data]);

  const updateKpi = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await driverService.updateKPISettings(id, kpi);
      toast.success('KPI settings updated');
      load();
    } catch {
      toast.error('Failed to update KPI settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;
  if (!data?.driver) return <div className="page-content"><p>Driver not found.</p></div>;

  const d = data.driver;
  const m = data.metrics || {};

  return (
    <div>
      <PageHeader
        title={`Driver: ${d.full_name}`}
        subtitle="Complete profile, leave history, financial trends and KPI performance"
        actions={<Link to="/drivers" className="btn btn-secondary">Back to Drivers</Link>}
      />

      <div className="grid-2" style={{ gap: '1.5rem' }}>
        <div className="card">
          <div className="card-header"><h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Driver Profile</h3></div>
          <div className="card-body">
            {[
              ['Full Name', d.full_name],
              ['Email', d.email || '—'],
              ['Phone', d.phone || '—'],
              ['National ID', d.national_id || '—'],
              ['License Number', d.license_number || '—'],
              ['License Expiry', d.license_expiry ? formatDate(d.license_expiry) : '—'],
              ['NHIF', d.nhif_number || '—'],
              ['Created At', d.created_at ? formatDateTime(d.created_at) : '—'],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-100)', fontSize: '0.875rem' }}>
                <span style={{ color: 'var(--gray-500)' }}>{k}</span>
                <span style={{ fontWeight: 500 }}>{v}</span>
              </div>
            ))}
            <div style={{ marginTop: '0.75rem' }}>
              <Badge status={d.status} label={d.status} />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Driver Performance Snapshot</h3></div>
          <div className="card-body">
            {[
              ['Trips', m.total_trips || 0],
              ['Leaves Taken', m.leaves_taken || 0],
              ['Leave Days', m.leave_days || 0],
              ['Revenue', formatCurrency(m.total_revenue)],
              ['Expenses', formatCurrency(m.total_expenses)],
              ['Profit', formatCurrency(m.total_profit)],
              ['Avg Profit / Trip', formatCurrency(m.avg_profit_per_trip)],
              ['Expense Ratio', `${m.expense_ratio_percent || 0}%`],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-100)', fontSize: '0.875rem' }}>
                <span style={{ color: 'var(--gray-500)' }}>{k}</span>
                <span style={{ fontWeight: 600 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header"><h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Monthly Revenue / Expense / Profit Trend</h3></div>
        <div className="card-body">
          {(data.trends || []).length === 0 ? <p style={{ color: 'var(--gray-500)' }}>No trend data yet.</p> : (data.trends || []).map((t, i) => (
            <div key={i} style={{ marginBottom: '0.9rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', marginBottom: '0.35rem' }}>
                <strong>{formatDate(t.month)}</strong>
                <span>Profit: {formatCurrency(t.profit)}</span>
              </div>
              <TrendBar value={t.revenue} max={monthlyMax} color="var(--info-text)" />
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.2rem' }}>Revenue: {formatCurrency(t.revenue)}</div>
              <TrendBar value={t.expenses} max={monthlyMax} color="var(--danger)" />
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.2rem' }}>Expenses: {formatCurrency(t.expenses)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header"><h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Expense Category Trend</h3></div>
        <div className="card-body">
          {(data.expense_trends || []).length === 0 ? <p style={{ color: 'var(--gray-500)' }}>No expense trend data yet.</p> : (data.expense_trends || []).map((t, i) => (
            <div key={i} style={{ marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', marginBottom: '0.35rem' }}>
                <span style={{ textTransform: 'capitalize' }}>{t.category.replace('_', ' ')}</span>
                <strong>{formatCurrency(t.total)}</strong>
              </div>
              <TrendBar value={t.total} max={expenseMax} color="var(--warning-text)" />
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header"><h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>KPI Settings and Conclusion</h3></div>
        <div className="card-body">
          <form onSubmit={updateKpi}>
            {isOwner && (
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Owner Monthly Profit Target (KES)</label>
                  <input className="form-control" type="number" value={kpi.owner_monthly_profit_target} onChange={(e) => setKpi(p => ({ ...p, owner_monthly_profit_target: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Owner Max Expense Ratio (%)</label>
                  <input className="form-control" type="number" value={kpi.owner_max_expense_ratio_percent} onChange={(e) => setKpi(p => ({ ...p, owner_max_expense_ratio_percent: e.target.value }))} />
                </div>
              </div>
            )}

            {isDriverSelf && (
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">My Monthly Profit Goal (KES)</label>
                  <input className="form-control" type="number" value={kpi.driver_monthly_profit_goal} onChange={(e) => setKpi(p => ({ ...p, driver_monthly_profit_goal: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">My Trip Target (Count)</label>
                  <input className="form-control" type="number" value={kpi.driver_target_trip_count} onChange={(e) => setKpi(p => ({ ...p, driver_target_trip_count: e.target.value }))} />
                </div>
              </div>
            )}

            {(isOwner || isDriverSelf) && (
              <div className="form-group">
                <label className="form-label">KPI Notes</label>
                <textarea className="form-control" rows={2} value={kpi.notes} onChange={(e) => setKpi(p => ({ ...p, notes: e.target.value }))} />
              </div>
            )}

            {(isOwner || isDriverSelf) && (
              <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save KPI Settings'}</button>
            )}
          </form>

          <div style={{ marginTop: '1rem', background: 'var(--info-light)', color: 'var(--info-text)', borderRadius: 'var(--radius-md)', padding: '0.75rem 1rem', fontSize: '0.875rem' }}>
            {data.conclusion}
          </div>
        </div>
      </div>
    </div>
  );
}
